import {ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PanelBaseComponent} from '../panels/panel-base.component';
import {UserSettingsService} from '../user-settings.service';
import {MessageBusService} from '../message-bus.service';
import {ActionBoardService, SuperUserPreferences} from './action-board.service';
import {ActionBoardComposerComponent} from './action-board-composer.component';
import {
    ActionBoardUserWireDto,
    ActionItemEventWireDto,
    ActionItemWireDto,
    ActionSeverity,
    ALL_USERS_KEY,
    ArchiveSortKey,
    ArchiveSortLink,
    GetActionBoardArchive
} from '../shell-communication/action-board-operations';
import {
    ALL_KEY,
    ARCHIVE_SEVERITY_OPTIONS,
    buildPortfolioOptions,
    withOwner,
    buildSourceOptions,
    DayBlock,
    FilterOption,
    groupByDay,
    isOutstanding,
    matchesFilter,
    pickerValueToDayKey,
    sourceLabel,
    SourceKey
} from './action-board.model';
import {isVoid} from '../utils';
import {shortUserName} from './action-item-view';

/**
 * CR-20260922-001 — the Action Board panel (§5.6). One client's board: day blocks newest first,
 * every outstanding item under today, scope tabs generated from the live subject paths, one card
 * selected at a time with its detail in a reading pane on the right (owner 2026-09-22, Session
 * Messages style). For a user with subordinates: the client switch in the header bar and the
 * composer button beside it. A manager's clicks are inert with respect to the client's
 * lifecycle: read/acknowledge are sent only when the board is the caller's own. Delivered is not
 * the panel's to write (CR-20260924-001 F1: the service writes it on every own-board load).
 *
 * Panel state (the selected client, the selected scope) is session-only: getState() returns null.
 * The board itself is server state and is re-read on every ActionBoardChanged push for the viewed
 * user, and every 30 s while a subordinate is viewed (their pushes go to THEIR terminals).
 *
 * The ARCHIVE face (owner screencast 2026-09-23): the "Archive" button at the right edge of the
 * header bar flips the panel to a flat, paged list of EVERY completed item of the viewed user, with
 * a Session-Messages-style filter column (completion date from/to, severity, state, search) and the
 * header filters (client, source, portfolio) still applied. Every filter runs on the server; the
 * page size is however many cards fit the list viewport (owner rulings 1–5). "Active" flips back.
 */
@Component({
    selector: 'ets-action-board-panel',
    templateUrl: './action-board-panel.component.html',
    styleUrls: ['./action-board-panel.component.scss'],
    host: {'[class.ets-hidden]': '!isActive'}
})
export class ActionBoardPanelComponent extends PanelBaseComponent {

    constructor(
        changeDetector: ChangeDetectorRef,
        userSettingsService: UserSettingsService,
        messageBus: MessageBusService,
        private readonly _service: ActionBoardService,
        private readonly _toastr: ToastrService
    ) {
        super(changeDetector, userSettingsService, messageBus);
    }

    private readonly _unsubscriber = new Subject<void>();
    private _pollHandle: any = null;

    @ViewChild(ActionBoardComposerComponent) composer: ActionBoardComposerComponent;

    isBoardLoading = false;
    loadError: string | null = null;

    /** The caller's own id, a subordinate's id, or ALL_USERS_KEY (CR-20260929-002: own + every subordinate). */
    viewedUserId: string;
    /** The user picked a client himself: the All default no longer applies (CR-20260929-002). */
    private _clientPicked = false;
    isOwnBoard = true;
    subordinates: ActionBoardUserWireDto[] = [];

    todayDayKey: string;
    items: ActionItemWireDto[] = [];
    blocks: DayBlock[] = [];
    // Source → portfolio filters (owner screencast 2026-09-23; replaced the scope tabs).
    sourceOptions: FilterOption[] = [];
    portfolioOptions: FilterOption[] = [];
    selectedSource: SourceKey = ALL_KEY;
    selectedPortfolio: string = ALL_KEY;
    selectedItemId: string | null = null;
    selectedRecord: ActionItemEventWireDto[] | null = null;

    // The reading pane (owner 2026-09-22): closed = list 100 / detail 0; open = the last split the user dragged.
    listSize = 100;
    detailSize = 0;
    private _openSplit: [number, number] = [58, 42];

    // ---- the Archive face (owner 2026-09-23)
    isArchive = false;
    isArchiveLoading = false;
    archiveItems: ActionItemWireDto[] = [];
    archiveTotal = 0;
    archivePageIndex = 0;
    /** Measured from the list viewport and the first rendered card; the server clamps to 1..200. */
    archivePageSize = 20;
    /** Portfolio dropdown entries for the archive: from the reply (the archive may hold portfolios the board no longer shows). */
    archivePortfolioOptions: FilterOption[] = [];
    readonly severityOptions = ARCHIVE_SEVERITY_OPTIONS;
    /** The filter column's values. Dates are the picker's ISO strings (day precision). */
    archiveFrom: string | null = null;
    archiveTo: string | null = null;
    archiveSeverities: ActionSeverity[] = [];
    archiveSearch = '';
    /** The sort row (owner 2026-09-23, second screencast + "cumulative, not exclusive"): a CHAIN of
     * attributes in click order. Click an inactive name = append it (newest / highest first); click
     * an active one = flip its direction; a third click = drop it. Empty chain = the server default,
     * completion time newest first (ruling 3). */
    archiveSort: ArchiveSortLink[] = [{key: 'completed', descending: true}];
    readonly archiveSortOptions: { key: ArchiveSortKey; text: string }[] = [
        {key: 'completed', text: 'Completed'},
        {key: 'originated', text: 'Originated'},
        {key: 'severity', text: 'Severity'}
    ];

    @ViewChild('archiveList') archiveListRef: ElementRef<HTMLElement>;
    private _archiveResizeObserver: any = null;
    private _archiveMeasureHandle: any = null;
    private _archiveRequestSeq = 0;

    get archivePageCount(): number {
        return this.archiveTotal === 0 ? 0 : Math.ceil(this.archiveTotal / this.archivePageSize);
    }

    get archiveRangeText(): string {
        if (this.archiveTotal === 0) {
            return 'No items';
        }
        const first = this.archivePageIndex * this.archivePageSize + 1;
        const last = Math.min(this.archiveTotal, first + this.archiveItems.length - 1);
        return `${first}–${last} of ${this.archiveTotal}`;
    }

    /** Any filter set in the archive face, the header's source / portfolio included (owner 2026-09-23: Clear All clears ALL). */
    get hasArchiveFilters(): boolean {
        return !!this.archiveFrom || !!this.archiveTo || this.archiveSeverities.length > 0
            || !!(this.archiveSearch || '').trim()
            || this.selectedSource !== ALL_KEY || this.selectedPortfolio !== ALL_KEY;
    }

    get ownUserId(): string {
        return this._service.ownUserId;
    }

    get hasSubordinates(): boolean {
        return this.subordinates.length > 0;
    }

    /** CR-20260929-002 (owner 2026-09-29): "All" sits first and is the default for a user with subordinates. */
    get clientChoices(): { userId: string, userName: string }[] {
        return [
            {userId: ALL_USERS_KEY, userName: 'All'},
            {userId: this.ownUserId, userName: 'Me'},
            ...this.subordinates.map(s => ({userId: s.userId, userName: shortUserName(s.userName)}))
        ];
    }

    get isAllView(): boolean {
        return this.viewedUserId === ALL_USERS_KEY;
    }

    /** The caller is this item's recipient. Read / unread / acknowledge are written for these only —
     * per item, because the All view mixes own and subordinates' cards (CR-20260929-002). */
    isOwnItem(item: ActionItemWireDto | null): boolean {
        return !!item && item.recipientUserId === this.ownUserId;
    }

    /** The owner chip, first on the card: shown in the All view only (owner 2026-09-29), in the
     * switch's own words — "Me" or the subordinate's short name. */
    ownerChip(item: ActionItemWireDto): string | null {
        return item ? this.ownerName(item.recipientUserId) : null;
    }

    /** The client's name in the All view — the chip and the portfolio prefix (owner 2026-09-29); null elsewhere. */
    ownerName(userId: string | null | undefined): string | null {
        if (!this.isAllView || !userId) {
            return null;
        }
        if (userId === this.ownUserId) {
            return 'Me';
        }
        const sub = this.subordinates.find(s => s.userId === userId);
        return sub ? shortUserName(sub.userName) : null;
    }

    get selectedItem(): ActionItemWireDto | null {
        if (!this.selectedItemId) {
            return null;
        }
        const list = this.isArchive ? this.archiveItems : this.items;
        return list.find(x => x.actionItemId === this.selectedItemId) || null;
    }

    get outstandingTotal(): number {
        return this.items.filter(isOutstanding).length;
    }

    // ---------------------------------------------------------------- lifecycle

    etsOnInit(): void {
        this.viewedUserId = this.ownUserId;

        this._service.boardChanged$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(dto => {
                // Only the own pushes reach this session; in the All view they refresh it too.
                if (dto.userId === this.viewedUserId || (this.isAllView && dto.userId === this.ownUserId)) {
                    this.reload().catch(e => console.error('[action-board] reload failed', e));
                    if (this.isArchive) {
                        // A completion lands in the archive at once: keep the shown page current.
                        this.loadArchive().catch(e => console.error('[action-board] archive reload failed', e));
                    }
                }
            });

        this.loadSubordinates().catch(e => console.error('[action-board] subordinates failed', e));
        this.reload().catch(e => console.error('[action-board] load failed', e));
    }

    etsAfterViewInit(): void {
        // isActive is assigned before the view exists; report the initial state once ids are set.
        this._service.reportBoardVisible(this.panelId, this.layoutTabId, this.isActive);
    }

    etsOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
        this.stopPolling();
        this.stopArchiveMeasuring();
        this._service.reportBoardVisible(this.panelId, this.layoutTabId, false);
    }

    protected getState(): any {
        return null;
    }

    protected setState(state: any): void {
    }

    protected onIsActiveChanged(): void {
        // The wash is for OTHER panels: while this board is the active tab, the overlay steps aside.
        this._service.reportBoardVisible(this.panelId, this.layoutTabId, this.isActive);
        if (this.isActive) {
            this.reload().catch(e => console.error('[action-board] reload failed', e));
        }
    }

    // ---------------------------------------------------------------- loading

    async reload(): Promise<void> {
        this.isBoardLoading = true;
        this.loadError = null;
        this._changeDetector.detectChanges();

        try {
            const reply = await this._service.loadBoard(this.viewedUserId === this.ownUserId ? null : this.viewedUserId);

            this.isOwnBoard = reply.isOwnBoard;
            this.todayDayKey = reply.todayDayKey;
            this.items = reply.items;
            this.rebuildView();
        } catch (e) {
            console.error('[action-board] load failed', e);
            this.loadError = 'The board could not be loaded.';
        } finally {
            this.isBoardLoading = false;
            this._changeDetector.detectChanges();
        }
    }

    private rebuildView(): void {
        this.sourceOptions = buildSourceOptions(this.items);
        this.portfolioOptions = buildPortfolioOptions(this.items, this.selectedSource,
            this.isAllView ? (id: string) => this.ownerName(id) : null);
        if (!this.isArchive && !this.portfolioOptions.some(o => o.key === this.selectedPortfolio)) {
            // The source no longer holds the chosen portfolio: back to All portfolios. (In the
            // archive the portfolio list is the reply's — loadArchive applies the same rule.)
            this.selectedPortfolio = ALL_KEY;
        }
        const scoped = this.items.filter(x => matchesFilter(x, this.selectedSource, this.selectedPortfolio));
        this.blocks = groupByDay(scoped, this.todayDayKey);

        if (this.selectedItemId && !this.items.some(x => x.actionItemId === this.selectedItemId)) {
            this.closeDetail();
        }
    }

    private async loadSubordinates(): Promise<void> {
        this.subordinates = await this._service.loadSubordinates();
        this._changeDetector.detectChanges();
        if (this.hasSubordinates && !this._clientPicked && this.viewedUserId === this.ownUserId) {
            // CR-20260929-002: All is the default for a user with subordinates.
            await this.switchClient(ALL_USERS_KEY);
        }
    }

    // ---------------------------------------------------------------- client switch

    async onViewedUserChanged(userId: string): Promise<void> {
        if (isVoid(userId) || userId === this.viewedUserId) {
            return;
        }
        this._clientPicked = true;
        await this.switchClient(userId);
    }

    private async switchClient(userId: string): Promise<void> {
        if (userId === this.viewedUserId) {
            return;
        }
        this.viewedUserId = userId;
        this.selectedSource = ALL_KEY;
        this.selectedPortfolio = ALL_KEY;
        this.closeDetail();
        this.stopPolling();
        await this.reload();
        if (this.isArchive) {
            this.archivePageIndex = 0;
            await this.loadArchive();
        }
        if (!this.isOwnBoard) {
            // A subordinate's pushes go to the subordinate's terminals, not to this session.
            this._pollHandle = setInterval(() => this.pollViewedUser(), 30000);
        }
    }

    private pollViewedUser(): void {
        this.reload().catch(() => undefined);
        if (this.isArchive) {
            this.loadArchive().catch(() => undefined);
        }
    }

    private stopPolling(): void {
        if (this._pollHandle) {
            clearInterval(this._pollHandle);
            this._pollHandle = null;
        }
    }

    // ---------------------------------------------------------------- source / portfolio filters

    onSourceChanged(key: SourceKey): void {
        if (!key || key === this.selectedSource) {
            return;   // the select box clearing itself, or a re-render echo
        }
        this.selectedSource = key;
        this.rebuildView();
        this._changeDetector.detectChanges();
        if (this.isArchive) {
            // The source shapes the portfolio list: the reply brings the new one.
            this.selectedPortfolio = ALL_KEY;
            this.archivePageIndex = 0;
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }
    }

    onPortfolioChanged(key: string): void {
        if (!key || key === this.selectedPortfolio) {
            return;
        }
        this.selectedPortfolio = key;
        this.rebuildView();
        this._changeDetector.detectChanges();
        if (this.isArchive) {
            this.archivePageIndex = 0;
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }
    }

    // ---------------------------------------------------------------- the Archive face

    /** The header button: "Archive" on the board, "Active" in the archive (owner ruling 5). */
    toggleArchive(): void {
        this.isArchive = !this.isArchive;
        this.closeDetail();
        if (this.isArchive) {
            this.archivePageIndex = 0;
            this._changeDetector.detectChanges();
            this.startArchiveMeasuring();
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        } else {
            this.stopArchiveMeasuring();
            // Back to the board exactly as it was: the header filters were never touched by the flip,
            // only the portfolio list is the board's again.
            this.rebuildView();
            this._changeDetector.detectChanges();
        }
    }

    async loadArchive(): Promise<void> {
        const seq = ++this._archiveRequestSeq;
        this.isArchiveLoading = true;
        this.loadError = null;

        try {
            this._changeDetector.detectChanges();
            const query = new GetActionBoardArchive(
                this.viewedUserId === this.ownUserId || this.isAllView ? null : this.viewedUserId,
                pickerValueToDayKey(this.archiveFrom),
                pickerValueToDayKey(this.archiveTo),
                this.archiveSeverities,
                this.selectedSource,
                this.selectedPortfolio,
                (this.archiveSearch || '').trim(),
                this.archiveSort,
                this.archivePageIndex,
                this.archivePageSize,
                this.isAllView
            );
            const reply = await this._service.loadArchive(query);
            if (seq !== this._archiveRequestSeq || !this.isArchive) {
                return;   // a later request (or the flip back) superseded this one
            }

            this.todayDayKey = reply.todayDayKey;
            this.isOwnBoard = reply.isOwnBoard;
            this.archiveItems = reply.items;
            this.archiveTotal = reply.totalCount;
            this.archivePageIndex = reply.pageIndex;

            // The archive's portfolios come from the reply, plain labels (counts are a board thing).
            // All view: each portfolio prefixed with its client (CR-20260929-002), sorted so a client's books sit together.
            const options: FilterOption[] = reply.portfolios
                .map(p => {
                    const label = withOwner(p.label, this.ownerName(p.ownerUserId));
                    return {key: p.path, label, outstanding: 0, text: label};
                })
                .sort((a, b) => a.label.localeCompare(b.label));
            options.unshift({key: ALL_KEY, label: 'All portfolios', outstanding: 0, text: 'All portfolios'});
            this.archivePortfolioOptions = options;
            if (!options.some(o => o.key === this.selectedPortfolio)) {
                this.selectedPortfolio = ALL_KEY;
            }

            if (this.selectedItemId && !this.archiveItems.some(x => x.actionItemId === this.selectedItemId)) {
                this.closeDetail();
            }
        } catch (e) {
            console.error('[action-board] archive load failed', e);
            this.loadError = 'The archive could not be loaded.';
        } finally {
            if (seq === this._archiveRequestSeq) {
                this.isArchiveLoading = false;
            }
            this._changeDetector.detectChanges();
            this.measureArchivePage();
        }
    }

    /** Source dropdown entries in the archive: the same five keys, plain labels. */
    get archiveSourceOptions(): FilterOption[] {
        const keys: SourceKey[] = [ALL_KEY, 'APG', 'HedgingGrid', 'PHAnalysis', 'Alerts'];
        return keys.map(key => ({key, label: sourceLabel(key), outstanding: 0, text: sourceLabel(key)}));
    }

    private _archiveLoadHandle: any = null;

    /**
     * A filter change re-queries page 1 — on the NEXT tick, one request per burst. Clear All sets
     * several controls by binding at once, and the date picker's value setter emits `valueChanged`
     * synchronously DURING change detection (owner smoke 2026-09-23: "Clear All resets the controls
     * but the list stays filtered"): a load started from inside another load's detectChanges() was
     * superseding the outer one's reply while never applying its own. Scheduling keeps every load
     * outside change detection and collapses the echoes into one query.
     */
    onArchiveFilterChanged(): void {
        if (!this.isArchive) {
            return;
        }
        this.archivePageIndex = 0;
        if (this._archiveLoadHandle) {
            return;   // a load is already scheduled for this tick; it will read the latest values
        }
        this._archiveLoadHandle = setTimeout(() => {
            this._archiveLoadHandle = null;
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }, 0);
    }

    onArchiveSeveritiesChanged(values: ActionSeverity[]): void {
        this.archiveSeverities = values || [];
        this.onArchiveFilterChanged();
    }

    private _searchHandle: any = null;

    /** Typed search: re-queried 300 ms after the last keystroke, not on every key. */
    onArchiveSearchChanged(value: string): void {
        const next = value || '';
        if (next === this.archiveSearch) {
            return;
        }
        this.archiveSearch = next;
        if (this._searchHandle) {
            clearTimeout(this._searchHandle);
        }
        this._searchHandle = setTimeout(() => {
            this._searchHandle = null;
            this.onArchiveFilterChanged();
        }, 300);
    }

    /** Inactive → appended descending; active descending → ascending; active ascending → removed. */
    onArchiveSort(key: ArchiveSortKey): void {
        const index = this.archiveSort.findIndex(s => s.key === key);
        if (index < 0) {
            this.archiveSort = [...this.archiveSort, {key, descending: true}];
        } else if (this.archiveSort[index].descending) {
            this.archiveSort = this.archiveSort.map((s, i) => i === index ? {key, descending: false} : s);
        } else {
            this.archiveSort = this.archiveSort.filter((_, i) => i !== index);
        }
        this.onArchiveFilterChanged();
    }

    /** 1-based position in the chain, 0 when inactive. */
    archiveSortRank(key: ArchiveSortKey): number {
        return this.archiveSort.findIndex(s => s.key === key) + 1;
    }

    archiveSortLink(key: ArchiveSortKey): ArchiveSortLink | null {
        return this.archiveSort.find(s => s.key === key) || null;
    }

    archiveSortHint(key: ArchiveSortKey, text: string): string {
        const link = this.archiveSortLink(key);
        if (!link) {
            return `Add ${text.toLowerCase()} to the sort`;
        }
        return link.descending ? 'Click to sort ascending' : 'Click to remove from the sort';
    }

    /** Every filter of the archive face back to "all" — dates, level, search AND source / portfolio
     * (owner 2026-09-23: "Clear All does not clear all filters"). The client switch and the sort stay. */
    clearArchiveFilters(): void {
        if (this._searchHandle) {
            clearTimeout(this._searchHandle);
            this._searchHandle = null;
        }
        this.archiveFrom = null;
        this.archiveTo = null;
        this.archiveSeverities = [];
        this.archiveSearch = '';
        this.selectedSource = ALL_KEY;
        this.selectedPortfolio = ALL_KEY;
        this.rebuildView();
        this.onArchiveFilterChanged();
    }

    archivePrevPage(): void {
        if (this.archivePageIndex > 0) {
            this.archivePageIndex--;
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }
    }

    archiveNextPage(): void {
        if (this.archivePageIndex + 1 < this.archivePageCount) {
            this.archivePageIndex++;
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }
    }

    /**
     * Page size = how many cards fit the list viewport without scrolling (owner ruling 4): the
     * viewport height over one rendered card's height (margins included). Re-measured on every
     * resize of the list; a changed size re-queries the page the first shown item falls on.
     */
    private measureArchivePage(): void {
        const host = this.archiveListRef?.nativeElement;
        if (!host || !this.isArchive) {
            return;
        }
        const viewport = host.clientHeight;
        if (viewport <= 0) {
            return;
        }
        const cards = host.querySelectorAll('ets-action-board-card');
        let rowHeight = 52;   // a card: 11px padding ×2 + one text line + the 6px host margin (action-board-card.component.scss)
        if (cards.length >= 2) {
            // The true pitch, vertical margins collapsed as the browser collapses them.
            const pitch = cards[1].getBoundingClientRect().top - cards[0].getBoundingClientRect().top;
            if (pitch > 0) {
                rowHeight = pitch;
            }
        } else if (cards.length === 1) {
            const rect = cards[0].getBoundingClientRect();
            const style = getComputedStyle(cards[0]);
            const margin = Math.max(parseFloat(style.marginTop) || 0, parseFloat(style.marginBottom) || 0);
            if (rect.height > 0) {
                rowHeight = rect.height + margin;
            }
        }
        const fit = Math.max(1, Math.min(200, Math.floor(viewport / rowHeight)));
        if (fit !== this.archivePageSize) {
            const firstShown = this.archivePageIndex * this.archivePageSize;
            this.archivePageSize = fit;
            this.archivePageIndex = Math.floor(firstShown / fit);
            this.loadArchive().catch(e => console.error('[action-board] archive load failed', e));
        }
    }

    private startArchiveMeasuring(): void {
        this.stopArchiveMeasuring();
        const host = this.archiveListRef?.nativeElement;
        const ResizeObserverCtor = (window as any).ResizeObserver;
        if (!host || !ResizeObserverCtor) {
            return;
        }
        this._archiveResizeObserver = new ResizeObserverCtor(() => {
            // Debounced: a drag of the split or the window fires many times a second.
            if (this._archiveMeasureHandle) {
                clearTimeout(this._archiveMeasureHandle);
            }
            this._archiveMeasureHandle = setTimeout(() => {
                this._archiveMeasureHandle = null;
                this.measureArchivePage();
            }, 200);
        });
        this._archiveResizeObserver.observe(host);
    }

    private stopArchiveMeasuring(): void {
        if (this._archiveLoadHandle) {
            clearTimeout(this._archiveLoadHandle);
            this._archiveLoadHandle = null;
        }
        if (this._archiveResizeObserver) {
            this._archiveResizeObserver.disconnect();
            this._archiveResizeObserver = null;
        }
        if (this._archiveMeasureHandle) {
            clearTimeout(this._archiveMeasureHandle);
            this._archiveMeasureHandle = null;
        }
    }

    // ---------------------------------------------------------------- cards

    async onSelect(item: ActionItemWireDto): Promise<void> {
        if (this.selectedItemId === item.actionItemId) {
            this.closeDetail();
            this._changeDetector.detectChanges();
            return;
        }

        this.selectedItemId = item.actionItemId;
        this.selectedRecord = null;
        this.listSize = this._openSplit[0];
        this.detailSize = this._openSplit[1];
        this._changeDetector.detectChanges();

        // The archive is read-only history: opening an archived card never writes read state.
        if (this.isOwnItem(item) && !item.readAtUtc && !this.isArchive) {
            try {
                await this._service.markRead(item.actionItemId);
                item.readAtUtc = new Date().toISOString();
                if (!item.deliveredAtUtc) {
                    item.deliveredAtUtc = item.readAtUtc;
                }
            } catch (e) {
                console.error('[action-board] read failed', e);
            }
        }

        if (this.isSuperUser) {
            // The record is shown to super users only; nobody else pays for the read.
            try {
                const record = await this._service.loadRecord(item.actionItemId);
                if (this.selectedItemId === item.actionItemId) {
                    this.selectedRecord = record;
                }
            } catch (e) {
                console.error('[action-board] record failed', e);
            }
        }

        this._changeDetector.detectChanges();
    }

    closeDetail(): void {
        this.selectedItemId = null;
        this.selectedRecord = null;
        this.listSize = 100;
        this.detailSize = 0;
        this._changeDetector.detectChanges();
    }

    onSplitDragEnd(ev: { sizes: number[] }): void {
        if (ev && ev.sizes && ev.sizes.length === 2 && this.selectedItemId) {
            this._openSplit = [ev.sizes[0], ev.sizes[1]];
            this.listSize = ev.sizes[0];
            this.detailSize = ev.sizes[1];
        }
    }

    async onAcknowledge(item: ActionItemWireDto): Promise<void> {
        if (!this.isOwnItem(item)) {
            return;
        }
        try {
            await this._service.acknowledge(item.actionItemId);
            await this.reload();
        } catch (e) {
            console.error('[action-board] acknowledge failed', e);
            this._toastr.error('The alert was not acknowledged');
        }
    }

    get isSuperUser(): boolean {
        return this._service.isSuperUser;
    }

    // ---------------------------------------------------------------- super user settings (CR-20260927-001)

    isSettingsOpen = false;

    get preferences(): SuperUserPreferences {
        return this._service.preferences;
    }

    /** The gear. The native click is stopped so the document listener below does not close the menu it just opened. */
    toggleSettingsMenu(e: any): void {
        if (e && e.event && e.event.stopPropagation) {
            e.event.stopPropagation();
        }
        this.isSettingsOpen = !this.isSettingsOpen;
        this._changeDetector.detectChanges();
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        if (this.isSettingsOpen) {
            this.isSettingsOpen = false;
            this._changeDetector.detectChanges();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.onDocumentClick();
    }

    onOverlaySwitched(value: boolean): void {
        if (value === this.preferences.overlayEnabled) {
            return;
        }
        this.applyPreferences({...this.preferences, overlayEnabled: !!value})
            .catch(e => console.error('[action-board] overlay switch failed', e));
    }

    onMessagesSwitched(value: boolean): void {
        if (value === this.preferences.messagesEnabled) {
            return;
        }
        this.applyPreferences({...this.preferences, messagesEnabled: !!value})
            .catch(e => console.error('[action-board] messages switch failed', e));
    }

    /** Stores the switches; the own board is re-read so the mute (or its lifting) shows at once. The menu stays open. */
    private async applyPreferences(next: SuperUserPreferences): Promise<void> {
        try {
            await this._service.setPreferences(next);
        } catch (e) {
            console.error('[action-board] settings were not saved', e);
            this._toastr.error('The settings were not saved');
        }
        this._changeDetector.detectChanges();
        if (this.isOwnBoard || this.isAllView) {
            await this.reload();
        }
    }

    /** Gmail-style "mark as unread" on an own, read item (owner 2026-09-22: any recipient; it goes in the record). */
    async onMarkUnread(item: ActionItemWireDto): Promise<void> {
        if (!this.isOwnItem(item) || !item.readAtUtc) {
            return;
        }
        try {
            await this._service.markUnread(item.actionItemId);
            item.readAtUtc = null;
            if (this.selectedItemId === item.actionItemId) {
                this.selectedRecord = await this._service.loadRecord(item.actionItemId);
            }
            this._changeDetector.detectChanges();
        } catch (e) {
            console.error('[action-board] mark unread failed', e);
            this._toastr.error('The item was not marked unread');
        }
    }

    /** F4a: the Open button is offered only when the target panel is granted to this user. */
    isOpenGranted(item: ActionItemWireDto | null): boolean {
        return this._service.isItemOpenGranted(item);
    }

    async onOpenSubject(item: ActionItemWireDto): Promise<void> {
        if (!this.isOpenGranted(item)) {
            const panelName = item.openPanel === 'HedgingGrid' ? 'the Hedging Grid'
                : item.openPanel === 'Collars' ? 'Portfolio Hedging' : item.openPanel;
            this._toastr.warning(`You have no access to ${panelName}`);
            return;
        }
        if (!this._service.openSubject(item, this.layoutTabId)) {
            this._toastr.warning('This item has no panel to open');
            return;
        }

        // Opening the panel acknowledges an outstanding ExpiringToday card on the recipient's OWN
        // board (owner 2026-09-23). The panel is already opening; the board follows.
        if (this.isOwnItem(item) && !this.isArchive && this._service.acknowledgesByOpening(item)) {
            try {
                await this._service.acknowledge(item.actionItemId, `opened in ${item.openPanel}`);
                await this.reload();
            } catch (e) {
                console.error('[action-board] acknowledge on open failed', e);
                this._toastr.error('The item was not marked done');
            }
        }
    }

    async onWithdraw(item: ActionItemWireDto): Promise<void> {
        if (!item.noticeId) {
            return;
        }
        try {
            await this._service.withdrawNotice(item.noticeId);
            this._toastr.success('Alert withdrawn');
            await this.reload();
        } catch (e) {
            console.error('[action-board] withdraw failed', e);
            this._toastr.error('The alert was not withdrawn');
        }
    }

    // ---------------------------------------------------------------- composer

    openComposer(): void {
        this.composer?.show(this.isOwnBoard || this.isAllView ? null : this.viewedUserId);
    }

    onNoticePosted(): void {
        this.reload().catch(e => console.error('[action-board] reload failed', e));
    }

    trackItem(_: number, item: ActionItemWireDto): string {
        return item.actionItemId;
    }

    trackBlock(_: number, block: DayBlock): string {
        return block.dayKey;
    }

    // ---------------------------------------------------------------- done drawer

    /** Days whose done drawer is open; collapsed by default, kept across refreshes (owner 2026-09-23). */
    private readonly _openDrawers = new Set<string>();

    isDrawerOpen(dayKey: string): boolean {
        return this._openDrawers.has(dayKey);
    }

    toggleDrawer(dayKey: string): void {
        if (this._openDrawers.has(dayKey)) {
            this._openDrawers.delete(dayKey);
        } else {
            this._openDrawers.add(dayKey);
        }
        this._changeDetector.detectChanges();
    }
}
